import { CardData, PlayerState, EnemyData, EnemyIntent, CombatResult, TurnAssignment, Buff } from '../types';
import { executeEnemyIntent } from '../enemies/EnemyAI';
import { detectCombos } from './ComboSystem';
import { Relic } from '../relics/Relic';
import { getCurseById } from '../cards/CurseRegistry';
import { FEATURES } from '../features';
import { hasTrait } from '../cards/CardTraits';
import {
  applyStatus,
  consumeEnemyWardedReduction,
  consumePlayerJammed,
  getDefenseMultiplierForPlayer,
  getIncomingDamageMultiplierForEnemy,
  getIncomingDamageMultiplierForPlayer,
} from './StatusSystem';
import { checkGuardBreak, getIncomingDamageMultiplier as getEnemyStanceIncomingMultiplier } from '../enemies/StanceSystem';
import { hasMutator } from '../enemies/MutatorRegistry';

interface ModifierBonus {
  extraDamage: number;
  extraBlock: number;
  heal: number;
  attackBuff: number;
  enemyDebuff: number;
  drawBonus: number;
  drawPenalty: number;
  zoneCapAttack: number;
  zoneCapDefense: number;
  zoneCapAbility: number;
  zoneCapAll: number;
}

interface ResolveTurnOptions {
  targetEnemy?: EnemyData;
}

function addBuff(player: PlayerState, buff: Buff): void {
  player.buffs.push(buff);
}

function getModifierBonus(
  card: CardData,
  trigger: 'attack' | 'defense' | 'ability',
  relics: Relic[] = [],
): ModifierBonus {
  const bonus: ModifierBonus = {
    extraDamage: 0,
    extraBlock: 0,
    heal: 0,
    attackBuff: 0,
    enemyDebuff: 0,
    drawBonus: 0,
    drawPenalty: 0,
    zoneCapAttack: 0,
    zoneCapDefense: 0,
    zoneCapAbility: 0,
    zoneCapAll: 0,
  };
  const abacusBead = relics.some(r => r.id === 'abacus_bead') ? 2 : 0;
  const arcaneLens = relics.some(r => r.id === 'arcane_lens') && trigger === 'ability' ? 1 : 0;
  const mirrorShard = relics.some(r => r.id === 'mirror_shard') && card.number < 0;

  for (const mod of card.modifiers) {
    if (mod.trigger !== trigger) continue;
    const val = mod.effect.value + abacusBead + arcaneLens;
    switch (mod.effect.type) {
      case 'extra_damage': bonus.extraDamage += val; break;
      case 'extra_block': bonus.extraBlock += val; break;
      case 'heal': bonus.heal += val; break;
      case 'buff_attack': bonus.attackBuff += val; break;
      case 'debuff_enemy': bonus.enemyDebuff += val; break;
      case 'draw': bonus.drawBonus += val; break;
      case 'draw_penalty': bonus.drawPenalty += val; break;
      case 'zone_cap_attack': bonus.zoneCapAttack += val; break;
      case 'zone_cap_defense': bonus.zoneCapDefense += val; break;
      case 'zone_cap_ability': bonus.zoneCapAbility += val; break;
      case 'zone_cap_all': bonus.zoneCapAll += val; break;
    }
  }

  if (mirrorShard && trigger === 'ability') {
    bonus.extraDamage += 2;
  }

  return bonus;
}

function applyIncomingPlayerDamageToEnemy(target: EnemyData, amount: number): number {
  if (amount <= 0) return 0;
  const damageAfterBlock = Math.max(0, amount - target.block);
  target.block = Math.max(0, target.block - amount);
  target.hp -= damageAfterBlock;
  return damageAfterBlock;
}

function applyScaledPlayerDamageToEnemy(target: EnemyData, amount: number): number {
  if (amount <= 0) return 0;
  let scaled = amount;

  if (FEATURES.enemyStances) {
    scaled = Math.floor(scaled * getEnemyStanceIncomingMultiplier(target));
  }

  if (FEATURES.statusSystem) {
    scaled = Math.floor(scaled * getIncomingDamageMultiplierForEnemy(target));
    scaled = Math.max(0, scaled - consumeEnemyWardedReduction(target));
  }

  return applyIncomingPlayerDamageToEnemy(target, scaled);
}

function applyBonusAsEffects(
  bonus: ModifierBonus,
  player: PlayerState,
  targetEnemy: EnemyData,
  result: CombatResult,
): number {
  let enemyDebuff = 0;

  if (bonus.attackBuff > 0) {
    result.abilityEffects.push({ type: 'buff_attack', value: bonus.attackBuff, description: `Attack +${bonus.attackBuff} this turn!` });
  }
  if (bonus.extraBlock > 0) {
    player.block += bonus.extraBlock;
    result.abilityEffects.push({ type: 'gain_block', value: bonus.extraBlock, description: `Gained ${bonus.extraBlock} block!` });
  }
  if (bonus.heal > 0) {
    const healed = Math.min(bonus.heal, player.maxHp - player.hp);
    player.hp += healed;
    result.playerHeal += healed;
    result.abilityEffects.push({ type: 'heal', value: healed, description: `Healed ${healed} HP!` });
  }
  if (bonus.enemyDebuff > 0) {
    enemyDebuff += bonus.enemyDebuff;
    result.abilityEffects.push({ type: 'debuff_enemy', value: bonus.enemyDebuff, description: `Enemy's next attack -${bonus.enemyDebuff}!` });
  }
  if (bonus.drawBonus > 0) {
    addBuff(player, { type: 'draw_bonus', value: bonus.drawBonus, duration: 1 });
    result.abilityEffects.push({ type: 'draw_bonus', value: bonus.drawBonus, description: `Draw +${bonus.drawBonus} next turn!` });
  }
  if (bonus.drawPenalty > 0) {
    addBuff(player, { type: 'draw_penalty', value: bonus.drawPenalty, duration: 1 });
    result.abilityEffects.push({ type: 'draw_penalty', value: bonus.drawPenalty, description: `Draw -${bonus.drawPenalty} next turn!` });
  }
  if (bonus.zoneCapAttack > 0) {
    addBuff(player, { type: 'zone_cap_attack', value: bonus.zoneCapAttack, duration: 1 });
    result.abilityEffects.push({ type: 'zone_cap_attack', value: bonus.zoneCapAttack, description: `ATK zone cap +${bonus.zoneCapAttack} next turn` });
  }
  if (bonus.zoneCapDefense > 0) {
    addBuff(player, { type: 'zone_cap_defense', value: bonus.zoneCapDefense, duration: 1 });
    result.abilityEffects.push({ type: 'zone_cap_defense', value: bonus.zoneCapDefense, description: `DEF zone cap +${bonus.zoneCapDefense} next turn` });
  }
  if (bonus.zoneCapAbility > 0) {
    addBuff(player, { type: 'zone_cap_ability', value: bonus.zoneCapAbility, duration: 1 });
    result.abilityEffects.push({ type: 'zone_cap_ability', value: bonus.zoneCapAbility, description: `ABL zone cap +${bonus.zoneCapAbility} next turn` });
  }
  if (bonus.zoneCapAll > 0) {
    addBuff(player, { type: 'zone_cap_all', value: bonus.zoneCapAll, duration: 1 });
    result.abilityEffects.push({ type: 'zone_cap_all', value: bonus.zoneCapAll, description: `All zone caps +${bonus.zoneCapAll} next turn` });
  }

  if (bonus.extraDamage > 0) {
    const dealt = applyScaledPlayerDamageToEnemy(targetEnemy, bonus.extraDamage);
    result.abilityEffects.push({ type: 'direct_damage', value: dealt, description: `Ability dealt ${dealt} damage!` });
    result.enemyDamageById![targetEnemy.id] = (result.enemyDamageById![targetEnemy.id] ?? 0) + dealt;
  }

  return enemyDebuff;
}

function applyCurseOnPlay(
  card: CardData,
  slot: 'attack' | 'defense' | 'ability',
  player: PlayerState,
  relics: Relic[],
  result: CombatResult,
): void {
  if (card.kind !== 'curse' || !card.curseId) return;
  if (slot === 'ability' && relics.some(r => r.id === 'curse_anvil')) {
    addBuff(player, { type: 'draw_bonus', value: 1, duration: 1 });
    result.abilityEffects.push({
      type: 'curse_anvil',
      value: 1,
      description: 'Curse Anvil: draw +1 next turn',
    });
    result.combatLog?.push('Curse Anvil empowers your next draw');
  }

  const curse = getCurseById(card.curseId);
  if (!curse?.onPlay || curse.onPlay.slot !== slot) return;

  if (curse.onPlay.hpLoss && curse.onPlay.hpLoss > 0) {
    player.hp = Math.max(0, player.hp - curse.onPlay.hpLoss);
    result.abilityEffects.push({
      type: 'curse_hp_loss',
      value: curse.onPlay.hpLoss,
      description: `${curse.name}: lose ${curse.onPlay.hpLoss} HP`,
    });
    result.combatLog?.push(`${curse.name} triggers: lose ${curse.onPlay.hpLoss} HP`);
  }

  if (curse.onPlay.drawPenaltyNextTurn && curse.onPlay.drawPenaltyNextTurn > 0) {
    addBuff(player, { type: 'draw_penalty', value: curse.onPlay.drawPenaltyNextTurn, duration: 1 });
    result.abilityEffects.push({
      type: 'curse_draw_penalty',
      value: curse.onPlay.drawPenaltyNextTurn,
      description: `${curse.name}: draw -${curse.onPlay.drawPenaltyNextTurn} next turn`,
    });
  }
}

export function resolveTurn(
  assignment: TurnAssignment,
  player: PlayerState,
  enemy: EnemyData,
  intent: EnemyIntent,
  relics: Relic[] = [],
  options: ResolveTurnOptions = {},
): CombatResult {
  const targetEnemy = options.targetEnemy ?? enemy;

  const result: CombatResult = {
    playerDamage: 0,
    enemyDamage: 0,
    playerHeal: 0,
    playerBlock: 0,
    enemyBlock: 0,
    abilityEffects: [],
    cardsExhausted: [],
    combos: [],
    combatLog: [],
    enemyDamageById: {},
    activeTargetId: targetEnemy.id,
  };

  const hasRelic = (id: string) => relics.some(r => r.id === id);

  const combos = detectCombos(assignment, relics);
  result.combos = combos;
  let comboDamage = 0;
  let comboBlock = 0;
  let comboHeal = 0;
  let comboDraw = 0;

  for (const combo of combos) {
    let dmgMult = 1;
    let blkMult = 1;
    let healMult = 1;
    if (hasRelic('combo_ring')) {
      dmgMult = 2;
      blkMult = 2;
      healMult = 2;
    }

    comboDamage += combo.bonusDamage * dmgMult;
    comboBlock += combo.bonusBlock * blkMult;
    comboHeal += combo.bonusHeal * healMult;
    comboDraw += combo.bonusDraw;

    if (hasRelic('perfect_lens') && combo.name === 'PERFECT 10') {
      comboHeal += 5;
    }

    if (combo.name === 'POLAR SPIKE' && FEATURES.cardTraits && FEATURES.statusSystem) {
      applyStatus(targetEnemy, 'brittle', 1, 1);
      result.combatLog?.push('POLAR SPIKE applies BRITTLE to target');
    }
  }

  if (hasRelic('echo_engine') && combos.length > 0) {
    const bonus = combos.length * 2;
    comboDamage += bonus;
    comboBlock += bonus;
    result.abilityEffects.push({
      type: 'echo_engine',
      value: combos.length,
      description: `Echo Engine: +${bonus} damage, +${bonus} block`,
    });
  }
  if (hasRelic('combo_archive') && combos.length > 0) {
    comboDraw += 1;
    result.abilityEffects.push({
      type: 'combo_archive',
      value: 1,
      description: 'Combo Archive: draw +1 next turn',
    });
  }

  if (comboHeal > 0) {
    const healed = Math.min(comboHeal, player.maxHp - player.hp);
    player.hp += healed;
    result.playerHeal += healed;
  }

  if (comboDraw > 0) {
    addBuff(player, { type: 'draw_bonus', value: comboDraw, duration: 1 });
  }

  const attackCommitted = assignment.attack.filter(card => card.kind === 'number').length;
  const defenseCommitted = assignment.defense.filter(card => card.kind === 'number').length;
  const abilityCommitted = assignment.ability.filter(card => card.kind === 'number').length;
  const totalCommitted = assignment.attack.length + assignment.defense.length + assignment.ability.length;
  const filledZones = [
    assignment.attack.length > 0,
    assignment.defense.length > 0,
    assignment.ability.length > 0,
  ].filter(Boolean).length;
  const playedNumberCards = [...assignment.attack, ...assignment.defense, ...assignment.ability]
    .filter(card => card.kind === 'number');
  const primeCardsPlayed = FEATURES.cardTraits
    ? playedNumberCards.filter(card => hasTrait(card, 'prime')).length
    : 0;
  const highPositiveAttackCards = FEATURES.cardTraits
    ? assignment.attack.filter(card => card.kind === 'number' && card.number > 0 && hasTrait(card, 'high')).length
    : 0;

  if (combos.length >= 2) {
    comboDamage += 3;
    comboBlock += 3;
    result.abilityEffects.push({
      type: 'chain_reaction',
      value: combos.length,
      description: `Chain Reaction x${combos.length}: +3 damage, +3 block`,
    });
  }
  if (combos.length >= 3) {
    comboDraw += 1;
    addBuff(player, { type: 'zone_cap_ability', value: 1, duration: 1 });
    result.abilityEffects.push({
      type: 'chain_overclock',
      value: 1,
      description: 'Chain Overclock: Draw +1, ABL cap +1 next turn',
    });
  }

  if (attackCommitted >= 2) {
    addBuff(player, { type: 'zone_cap_attack', value: 1, duration: 1 });
    result.abilityEffects.push({ type: 'formation_attack', value: 1, description: 'Momentum: ATK zone cap +1 next turn' });
  }
  if (defenseCommitted >= 2) {
    addBuff(player, { type: 'zone_cap_defense', value: 1, duration: 1 });
    result.abilityEffects.push({ type: 'formation_defense', value: 1, description: 'Momentum: DEF zone cap +1 next turn' });
  }
  if (abilityCommitted >= 2) {
    addBuff(player, { type: 'draw_bonus', value: 1, duration: 1 });
    result.abilityEffects.push({ type: 'formation_ability', value: 1, description: 'Momentum: Draw +1 next turn' });
  }
  if (filledZones === 3 && totalCommitted >= 4) {
    addBuff(player, { type: 'draw_bonus', value: 1, duration: 1 });
    addBuff(player, { type: 'zone_cap_all', value: 1, duration: 1 });
    result.abilityEffects.push({
      type: 'battle_rhythm',
      value: 1,
      description: 'Battle Rhythm: all zones engaged (Draw +1, all caps +1 next turn)',
    });
  }

  const strengthBuff = player.buffs.find(b => b.type === 'strength');
  let attackBoost = strengthBuff ? strengthBuff.value : 0;
  let enemyDebuffTotal = 0;

  let jamFirstAbility = consumePlayerJammed(player);
  if (hasMutator(enemy, 'static_field') && assignment.ability.length < 2) {
    jamFirstAbility = true;
    result.combatLog?.push('Static Field jams your first ability card');
  }
  let consumedAbilityJammer = false;
  let activeAbilityModifierCards = 0;
  let riskyAbilityCards = 0;
  let riskyAbilityCurses = 0;
  let shatteredGuard = false;

  for (const card of assignment.ability) {
    applyCurseOnPlay(card, 'ability', player, relics, result);
    const hasAbilityModifiers = card.modifiers.some(mod => mod.trigger === 'ability');
    const isRiskCard = card.kind === 'curse' || (card.kind === 'number' && card.number < 0);
    if (isRiskCard) {
      riskyAbilityCards += 1;
      if (card.kind === 'curse') riskyAbilityCurses += 1;
    }

    const cardIsJammed: boolean = jamFirstAbility && !consumedAbilityJammer;
    consumedAbilityJammer = consumedAbilityJammer || cardIsJammed;

    if (!cardIsJammed) {
      const bonus = getModifierBonus(card, 'ability', relics);
      enemyDebuffTotal += applyBonusAsEffects(bonus, player, targetEnemy, result);
      attackBoost += bonus.attackBuff;
      if (hasAbilityModifiers) {
        activeAbilityModifierCards += 1;
      }
    } else {
      result.combatLog?.push('A jammed ability card failed to trigger modifiers');
    }

    result.cardsExhausted.push(card.id);
  }

  if (riskyAbilityCards > 0) {
    addBuff(player, { type: 'draw_bonus', value: riskyAbilityCards, duration: 1 });
    result.abilityEffects.push({
      type: 'risk_cycle_draw',
      value: riskyAbilityCards,
      description: `Risk Cycle: draw +${riskyAbilityCards} next turn`,
    });

    if (riskyAbilityCurses > 0) {
      player.hp = Math.max(0, player.hp - riskyAbilityCurses);
      result.abilityEffects.push({
        type: 'risk_cycle_backlash',
        value: riskyAbilityCurses,
        description: `Risk Backlash: lose ${riskyAbilityCurses} HP`,
      });
      result.combatLog?.push(`Risk Backlash: lose ${riskyAbilityCurses} HP`);
    }

    const surgeDamage = applyScaledPlayerDamageToEnemy(targetEnemy, riskyAbilityCards * 2);
    if (surgeDamage > 0) {
      result.abilityEffects.push({
        type: 'risk_cycle_damage',
        value: surgeDamage,
        description: `Risk Surge hits ${targetEnemy.name} for ${surgeDamage}`,
      });
      result.enemyDamageById![targetEnemy.id] = (result.enemyDamageById![targetEnemy.id] ?? 0) + surgeDamage;
    }
  }

  if (FEATURES.enemyStances && targetEnemy.stance === 'guard' && highPositiveAttackCards > 0 && activeAbilityModifierCards > 0) {
    shatteredGuard = true;
    const strippedBlock = targetEnemy.block;
    if (strippedBlock > 0) {
      targetEnemy.block = 0;
      result.abilityEffects.push({
        type: 'shatter_guard',
        value: strippedBlock,
        description: `Shatter Guard: remove ${strippedBlock} enemy block`,
      });
    }
    if (FEATURES.statusSystem) {
      applyStatus(targetEnemy, 'brittle', 1, 2);
    }
    result.combatLog?.push('Shatter Guard: high ATK + ability support breaks guard shell');
  }

  if (riskyAbilityCards >= 2 && FEATURES.statusSystem) {
    applyStatus(targetEnemy, 'brittle', 1, 1);
    applyStatus(player, 'exposed', 1, 1);
    result.combatLog?.push('Risk Escalation: both sides destabilized');
  }

  if (FEATURES.enemyStances && enemy.stance === 'aggro' && defenseCommitted >= 2 && attackCommitted >= 1) {
    addBuff(player, { type: 'zone_cap_defense', value: 1, duration: 1 });
    addBuff(player, { type: 'draw_bonus', value: 1, duration: 1 });
    result.abilityEffects.push({
      type: 'counter_formation',
      value: 1,
      description: 'Counter Formation: DEF cap +1, draw +1 next turn',
    });
    result.combatLog?.push('Counter Formation primed against AGGRO');
  }

  let defenseTotal = 0;
  for (const card of assignment.defense) {
    applyCurseOnPlay(card, 'defense', player, relics, result);

    let cardBlock = card.kind === 'number' ? card.number : 0;
    if (hasRelic('leather_shield')) cardBlock += (cardBlock > 0 ? 1 : 0);
    if (hasRelic('number_crown') && Math.abs(card.number) >= 8 && card.kind === 'number') cardBlock += 2;
    if (hasRelic('battle_standard') && card.kind === 'number' && card.number > 0 && cardBlock > 0) cardBlock += 1;
    if (hasRelic('bulwark_emblem') && card.kind === 'number' && card.number > 0 && card.number <= 3 && cardBlock > 0) cardBlock += 2;
    if (hasRelic('stance_compass') && enemy.stance === 'aggro' && cardBlock > 0) cardBlock += 2;
    defenseTotal += cardBlock;

    const bonus = getModifierBonus(card, 'defense', relics);
    defenseTotal += bonus.extraBlock;

    if (bonus.heal > 0) {
      const healed = Math.min(bonus.heal, player.maxHp - player.hp);
      player.hp += healed;
      result.playerHeal += healed;
      result.abilityEffects.push({ type: 'heal', value: healed, description: `Defense healed ${healed}!` });
    }

    if (bonus.extraDamage > 0) {
      const dealt = applyScaledPlayerDamageToEnemy(targetEnemy, bonus.extraDamage);
      result.abilityEffects.push({ type: 'thorns', value: dealt, description: `Thorns dealt ${dealt}!` });
      result.enemyDamageById![targetEnemy.id] = (result.enemyDamageById![targetEnemy.id] ?? 0) + dealt;
    }

    if (bonus.drawBonus > 0) addBuff(player, { type: 'draw_bonus', value: bonus.drawBonus, duration: 1 });
    if (bonus.drawPenalty > 0) addBuff(player, { type: 'draw_penalty', value: bonus.drawPenalty, duration: 1 });
    if (bonus.zoneCapAttack > 0) addBuff(player, { type: 'zone_cap_attack', value: bonus.zoneCapAttack, duration: 1 });
    if (bonus.zoneCapDefense > 0) addBuff(player, { type: 'zone_cap_defense', value: bonus.zoneCapDefense, duration: 1 });
    if (bonus.zoneCapAbility > 0) addBuff(player, { type: 'zone_cap_ability', value: bonus.zoneCapAbility, duration: 1 });
    if (bonus.zoneCapAll > 0) addBuff(player, { type: 'zone_cap_all', value: bonus.zoneCapAll, duration: 1 });
    enemyDebuffTotal += bonus.enemyDebuff;

    result.cardsExhausted.push(card.id);
  }

  defenseTotal += comboBlock;
  if (FEATURES.statusSystem) {
    defenseTotal = Math.floor(defenseTotal * getDefenseMultiplierForPlayer(player));
  }

  if (defenseTotal > 0) {
    player.block += defenseTotal;
  } else if (defenseTotal < 0) {
    if (hasRelic('thick_skin') && assignment.defense.length > 0) {
      player.block = Math.max(1, player.block + defenseTotal);
    } else {
      player.block = Math.max(0, player.block + defenseTotal);
    }
  }

  if (defenseTotal > 0 && defenseCommitted >= 2 && activeAbilityModifierCards >= 1) {
    const channelDamage = Math.max(1, Math.floor(defenseTotal / 4));
    const dealt = applyScaledPlayerDamageToEnemy(targetEnemy, channelDamage);
    if (dealt > 0) {
      result.abilityEffects.push({
        type: 'bulwark_channel',
        value: dealt,
        description: `Bulwark Channel deals ${dealt} to ${targetEnemy.name}`,
      });
      result.enemyDamageById![targetEnemy.id] = (result.enemyDamageById![targetEnemy.id] ?? 0) + dealt;
    }
  }

  result.playerBlock = player.block;

  let attackTotal = 0;
  let positiveAttackSum = 0;
  let guardBroken = false;
  for (const card of assignment.attack) {
    applyCurseOnPlay(card, 'attack', player, relics, result);

    if (hasMutator(enemy, 'thorned')) {
      player.hp = Math.max(0, player.hp - 1);
      result.combatLog?.push('Thorned mutator: you take 1 recoil');
    }

    let cardDamage = (card.kind === 'number' ? card.number : 0) + attackBoost;
    if (hasRelic('whetstone') && cardDamage > 0) cardDamage += 1;
    if (hasRelic('glass_cannon') && cardDamage > 0) cardDamage += 3;
    if (hasRelic('number_crown') && Math.abs(card.number) <= 3 && cardDamage > 0 && card.kind === 'number') cardDamage += 2;
    if (hasRelic('battle_standard') && card.kind === 'number' && card.number > 0 && cardDamage > 0) cardDamage += 1;
    if (hasRelic('hunter_mark') && card.kind === 'number' && card.number >= 8 && cardDamage > 0) cardDamage += 2;
    if (hasRelic('stance_compass') && targetEnemy.stance === 'cast' && cardDamage > 0) cardDamage += 3;
    if (hasRelic('infinity_loop') && card.kind === 'number' && card.number < 0) {
      cardDamage = Math.abs(card.number);
    }

    const bonus = getModifierBonus(card, 'attack', relics);
    cardDamage += bonus.extraDamage;

    if (cardDamage > 0) positiveAttackSum += cardDamage;

    if (bonus.heal > 0) {
      const healed = Math.min(bonus.heal, player.maxHp - player.hp);
      player.hp += healed;
      result.playerHeal += healed;
    }

    if (bonus.drawBonus > 0) addBuff(player, { type: 'draw_bonus', value: bonus.drawBonus, duration: 1 });
    if (bonus.drawPenalty > 0) addBuff(player, { type: 'draw_penalty', value: bonus.drawPenalty, duration: 1 });
    if (bonus.zoneCapAttack > 0) addBuff(player, { type: 'zone_cap_attack', value: bonus.zoneCapAttack, duration: 1 });
    if (bonus.zoneCapDefense > 0) addBuff(player, { type: 'zone_cap_defense', value: bonus.zoneCapDefense, duration: 1 });
    if (bonus.zoneCapAbility > 0) addBuff(player, { type: 'zone_cap_ability', value: bonus.zoneCapAbility, duration: 1 });
    if (bonus.zoneCapAll > 0) addBuff(player, { type: 'zone_cap_all', value: bonus.zoneCapAll, duration: 1 });
    enemyDebuffTotal += bonus.enemyDebuff;

    attackTotal += cardDamage;
    result.cardsExhausted.push(card.id);
  }

  attackTotal += comboDamage;

  if (attackTotal > 0) {
    guardBroken = FEATURES.enemyStances ? checkGuardBreak(targetEnemy, attackCommitted, positiveAttackSum) : false;
    if (guardBroken) {
      result.combatLog?.push('Guard Break! Target loses guard mitigation and becomes BRITTLE');
    }

    let finalAttack = attackTotal;
    if (FEATURES.enemyStances) {
      let stanceMultiplier = getEnemyStanceIncomingMultiplier(targetEnemy);
      if ((guardBroken || shatteredGuard) && targetEnemy.stance === 'guard') {
        stanceMultiplier = 1;
      }
      finalAttack = Math.floor(finalAttack * stanceMultiplier);
    }

    if (FEATURES.statusSystem) {
      finalAttack = Math.floor(finalAttack * getIncomingDamageMultiplierForEnemy(targetEnemy));
      finalAttack = Math.max(0, finalAttack - consumeEnemyWardedReduction(targetEnemy));
    }

    const dealt = applyIncomingPlayerDamageToEnemy(targetEnemy, finalAttack);
    result.enemyDamage = dealt;
    result.enemyDamageById![targetEnemy.id] = (result.enemyDamageById![targetEnemy.id] ?? 0) + dealt;
  } else if (attackTotal < 0) {
    targetEnemy.hp = Math.min(targetEnemy.maxHp, targetEnemy.hp - attackTotal);
    result.enemyDamage = attackTotal;
  }

  if (enemy.hp > 0) {
    let modifiedIntent = { ...intent };
    if (modifiedIntent.type === 'attack') {
      modifiedIntent.value = Math.max(0, modifiedIntent.value - enemyDebuffTotal);
    }

    if (FEATURES.enemyStances) {
      if (enemy.stance === 'aggro' && modifiedIntent.type === 'attack' && modifiedIntent.value >= 5 && defenseTotal >= modifiedIntent.value) {
        addBuff(player, { type: 'draw_bonus', value: 1, duration: 1 });
        if (FEATURES.statusSystem) {
          applyStatus(enemy, 'weakened', 1, 2);
        }
        result.abilityEffects.push({
          type: 'aggro_counter',
          value: 1,
          description: 'Counterplay: Full Brace vs AGGRO (enemy WEAKENED, draw +1)',
        });
        result.combatLog?.push('Counterplay: Full Brace lands on AGGRO');
      }

      if (guardBroken && targetEnemy.stance === 'guard') {
        addBuff(player, { type: 'zone_cap_attack', value: 1, duration: 1 });
        result.abilityEffects.push({
          type: 'guard_break_momentum',
          value: 1,
          description: 'Guard Break momentum: ATK cap +1 next turn',
        });
      }

      const castDisruptByAbility = activeAbilityModifierCards >= 2;
      const castDisruptByPrimeLock = primeCardsPlayed >= 2 && filledZones >= 2;
      const canDisruptCast = enemy.stance === 'cast'
        && modifiedIntent.type === 'special'
        && (castDisruptByAbility || castDisruptByPrimeLock);
      if (canDisruptCast) {
        modifiedIntent = { type: 'defend', value: 0, description: 'Cast disrupted' };
        if (FEATURES.statusSystem) {
          applyStatus(enemy, 'weakened', 1, 1);
        }

        const disruptDamage = applyScaledPlayerDamageToEnemy(enemy, 4);
        if (disruptDamage > 0) {
          const disruptLabel = castDisruptByPrimeLock ? 'Prime Lock' : 'Cast Disrupt';
          result.abilityEffects.push({
            type: 'cast_disrupt',
            value: disruptDamage,
            description: `${disruptLabel}: deal ${disruptDamage} to ${enemy.name}`,
          });
          result.enemyDamageById![enemy.id] = (result.enemyDamageById![enemy.id] ?? 0) + disruptDamage;
        }
        result.combatLog?.push(castDisruptByPrimeLock
          ? 'Prime Lock: cast disrupted with prime pattern'
          : 'Cast Disrupted: enemy spell fizzles');
      }
    }

    const enemyResult = executeEnemyIntent(enemy, modifiedIntent, player.block, player);
    let dmgToPlayer = enemyResult.damageToPlayer;

    if (FEATURES.statusSystem) {
      dmgToPlayer = Math.floor(dmgToPlayer * getIncomingDamageMultiplierForPlayer(player));
    }

    if (hasRelic('glass_cannon') && dmgToPlayer > 0) dmgToPlayer += 1;

    result.playerDamage = dmgToPlayer;
    player.hp -= result.playerDamage;
    enemy.block += enemyResult.enemyBlockGain;
    result.enemyBlock = enemy.block;

    if (enemyResult.combatLog && enemyResult.combatLog.length > 0) {
      result.combatLog?.push(...enemyResult.combatLog);
    }
  }

  return result;
}
